import { supabase } from "../supabase/supabaseClient";

/**
 * Get all highlights for a user
 */
export const getHighlights = async (userId) => {
    return await supabase
        .from("player_highlights")
        .select("*")
        .eq("user_id", userId)
        .order("display_order", { ascending: true });
};

/**
 * Create a new highlight
 */
export const createHighlight = async (userId, highlight) => {
    // Auto-detect platform from URL
    const platform = detectPlatform(highlight.url);
    
    return await supabase
        .from("player_highlights")
        .insert([{ 
            user_id: userId, 
            ...highlight,
            platform 
        }])
        .select()
        .single();
};

/**
 * Update a highlight
 */
export const updateHighlight = async (highlightId, updates) => {
    // Re-detect platform if URL changed
    if (updates.url) {
        updates.platform = detectPlatform(updates.url);
    }
    
    return await supabase
        .from("player_highlights")
        .update(updates)
        .eq("id", highlightId)
        .select()
        .single();
};

/**
 * Delete a highlight
 */
export const deleteHighlight = async (highlightId) => {
    return await supabase
        .from("player_highlights")
        .delete()
        .eq("id", highlightId);
};

/**
 * Reorder highlights (update display_order for multiple items)
 */
export const reorderHighlights = async (highlights) => {
    const updates = highlights.map((h, index) => ({
        id: h.id,
        display_order: index
    }));
    
    // Update each in sequence (Supabase doesn't support bulk update easily)
    for (const update of updates) {
        await supabase
            .from("player_highlights")
            .update({ display_order: update.display_order })
            .eq("id", update.id);
    }
    
    return { error: null };
};

/**
 * Detect video platform from URL
 */
export const detectPlatform = (url) => {
    if (!url) return "other";
    const lower = url.toLowerCase();
    
    if (lower.includes("youtube.com") || lower.includes("youtu.be")) return "youtube";
    if (lower.includes("vimeo.com")) return "vimeo";
    if (lower.includes("instagram.com")) return "instagram";
    if (lower.includes("tiktok.com")) return "tiktok";
    if (lower.includes("twitter.com") || lower.includes("x.com")) return "twitter";
    if (lower.includes("facebook.com") || lower.includes("fb.watch")) return "facebook";
    
    return "other";
};

/**
 * Extract video ID for embedding
 */
export const getVideoId = (url, platform) => {
    if (!url) return null;
    
    try {
        if (platform === "youtube") {
            // Handle youtube.com/watch?v=ID and youtu.be/ID
            const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
            return match ? match[1] : null;
        }
        if (platform === "vimeo") {
            const match = url.match(/vimeo\.com\/(\d+)/);
            return match ? match[1] : null;
        }
    } catch {
        return null;
    }
    
    return null;
};

/**
 * Get embed URL for supported platforms
 */
export const getEmbedUrl = (url, platform) => {
    const videoId = getVideoId(url, platform);
    
    if (platform === "youtube" && videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
    }
    if (platform === "vimeo" && videoId) {
        return `https://player.vimeo.com/video/${videoId}`;
    }
    
    return null; // Not embeddable, will open in new tab
};

/**
 * Get thumbnail URL for video
 */
export const getThumbnailUrl = (url, platform) => {
    const videoId = getVideoId(url, platform);
    
    if (platform === "youtube" && videoId) {
        return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
    }
    
    // Vimeo requires API call for thumbnail, so we'll use a placeholder for now
    return null;
};

/**
 * Get platform display info (icon, color, label)
 */
export const getPlatformInfo = (platform) => {
    const platforms = {
        youtube: { label: "YouTube", color: "#FF0000", icon: "▶" },
        vimeo: { label: "Vimeo", color: "#1AB7EA", icon: "▶" },
        instagram: { label: "Instagram", color: "#E4405F", icon: "◐" },
        tiktok: { label: "TikTok", color: "#000000", icon: "♪" },
        twitter: { label: "X/Twitter", color: "#1DA1F2", icon: "X" },
        facebook: { label: "Facebook", color: "#1877F2", icon: "f" },
        other: { label: "Video", color: "#666666", icon: "▶" }
    };
    
    return platforms[platform] || platforms.other;
};
